// Skill picker order: skills tagged with a chosen role type first, then skills
// for the chosen role family AND industry, the role family's, the industry's,
// then generic skills (no families, industries or role types: soft skills,
// everyday tools), then everything else.
// Within a tier, groups follow SKILL_CATEGORIES order and names sort A–Z.
import { SKILL_CATEGORIES, type SkillCategory } from '../enums.ts';
import { SKILL_CATEGORY_LABELS } from './labels.ts';

export interface PickableSkill {
  id: string;
  name: string;
  category: string | null;
  families: string[];
  /** Industry ids; empty = any industry. */
  industries: string[];
  /** Role type ids; empty = not tied to a role type. */
  roleTypes: string[];
}

export interface SkillGroup<S extends PickableSkill> {
  /** Unique across the result, so it can key a list. */
  heading: string;
  skills: S[];
}

const categoryLabel = (c: string | null) =>
  c && (SKILL_CATEGORIES as readonly string[]).includes(c)
    ? SKILL_CATEGORY_LABELS[c as SkillCategory]
    : 'Other';
const CATEGORY_ORDER = [...SKILL_CATEGORIES.map((c) => SKILL_CATEGORY_LABELS[c]), 'Other'];

const byName = <S extends { name: string }>(a: S, b: S) => a.name.localeCompare(b.name);

function byCategory<S extends PickableSkill>(skills: S[], suffix = ''): SkillGroup<S>[] {
  const map = new Map<string, S[]>();
  for (const s of skills) {
    const h = categoryLabel(s.category);
    map.set(h, [...(map.get(h) ?? []), s]);
  }
  return CATEGORY_ORDER.filter((h) => map.has(h)).map((h) => ({
    heading: `${h}${suffix}`,
    skills: map.get(h)!.sort(byName),
  }));
}

const flat = <S extends PickableSkill>(heading: string, skills: S[]): SkillGroup<S>[] =>
  skills.length ? [{ heading, skills: skills.sort(byName) }] : [];

export interface SkillContext {
  /** Role families of the role(s) chosen so far; empty = none yet. */
  families: readonly string[];
  /** Chosen industries (id + name); empty = none yet. */
  industries: readonly { id: string; name: string }[];
  /** Chosen role types (id + name); empty = none yet. */
  roleTypes: readonly { id: string; name: string }[];
}

/** Heading for the skills tagged with the chosen role types. */
export const suggestedHeading = (roleTypes: readonly { name: string }[]) =>
  `Suggested for ${roleTypes.length === 1 ? roleTypes[0]!.name : 'your roles'}`;

/**
 * Skills tagged with any of `roleTypeIds`, those also tagged with a chosen
 * industry first, then A–Z. Drives the one-click chips; `exclude` (ids already
 * picked) is applied before `limit`.
 */
export function suggestedSkills<S extends PickableSkill>(
  skills: readonly S[],
  roleTypeIds: readonly string[],
  industryIds: readonly string[],
  limit = 12,
  exclude: ReadonlySet<string> = new Set(),
): S[] {
  const rt = new Set(roleTypeIds);
  const ind = new Set(industryIds);
  const inIndustry = (s: S) => s.industries.some((x) => ind.has(x));
  return skills
    .filter((s) => !exclude.has(s.id) && s.roleTypes.some((x) => rt.has(x)))
    .sort((a, b) => Number(inIndustry(b)) - Number(inIndustry(a)) || byName(a, b))
    .slice(0, limit);
}

/** Heading for the chosen industries: its name, or a generic one for several. */
export const industryHeading = (industries: readonly { name: string }[]) =>
  industries.length === 1 ? industries[0]!.name : 'Your industries';

export function groupSkillsFor<S extends PickableSkill>(
  skills: readonly S[],
  { families, industries, roleTypes }: SkillContext,
): SkillGroup<S>[] {
  const fam = new Set(families);
  const ind = new Set(industries.map((i) => i.id));
  const rt = new Set(roleTypes.map((r) => r.id));
  const suggested: S[] = [];
  const both: S[] = [];
  const forRole: S[] = [];
  const forIndustry: S[] = [];
  const generic: S[] = [];
  const rest: S[] = [];
  for (const s of skills) {
    if (s.roleTypes.some((x) => rt.has(x))) {
      suggested.push(s);
      continue;
    }
    const f = s.families.some((x) => fam.has(x));
    const i = s.industries.some((x) => ind.has(x));
    if (f && i) both.push(s);
    else if (f) forRole.push(s);
    else if (i) forIndustry.push(s);
    else if (s.families.length === 0 && s.industries.length === 0 && s.roleTypes.length === 0)
      generic.push(s);
    else rest.push(s);
  }
  const industryName = industryHeading(industries);
  // Nothing chosen yet: nothing is "other", the rest keeps plain headings.
  const suffix = fam.size || ind.size || rt.size ? ' · other' : '';
  return [
    ...flat(suggestedHeading(roleTypes), suggested),
    ...flat(`${industryName} · ${[...fam].join(', ')}`, both),
    ...byCategory(forRole),
    ...flat(industryName, forIndustry),
    ...flat('General', generic),
    ...byCategory(rest, suffix),
  ];
}
